import { activePlan } from '@/features/plan/lib/active-plan'
import {
  ContentFormat,
  StartFlowType,
  type UserQuery,
} from '@/generated/graphql'
import type { ReturnFlowType } from './return-flow-machine'

export { activePlan }

type UserData = UserQuery['me']

const FORCE_PARAM = 'returnFlow'

// Dev-only escape hatch for QA: the flow is served once per user for the lifetime
// of the experiment, so without this the UI can only be seen by clearing the
// enrolment row in the database.
function forcedType(): ReturnFlowType | null {
  if (!import.meta.env.DEV) return null
  try {
    const params = new URLSearchParams(window.location.search)
    if (!params.has(FORCE_PARAM)) return null
    return params.get(FORCE_PARAM) === 'v3'
      ? StartFlowType.ReturnFlowDormant
      : StartFlowType.ReturnFlow
  } catch {
    return null
  }
}

// Strict: a type this build doesn't know is no verdict, never a wrong flow.
export function isReturnFlowType(
  type: StartFlowType | null | undefined,
): type is ReturnFlowType {
  return (
    type === StartFlowType.ReturnFlow ||
    type === StartFlowType.ReturnFlowDormant
  )
}

// The flow the takeover runs, or null when there is none to run.
export function returnFlowType(
  user: UserData | undefined,
): ReturnFlowType | null {
  const plan = activePlan(user)
  // Checked before the override so QA can never force the flow onto a plan the
  // server would refuse it for.
  if (plan?.contentFormat !== ContentFormat.Journey) return null
  const forced = forcedType()
  if (forced) return forced
  const served = plan.startFlow?.type
  return isReturnFlowType(served) ? served : null
}

export function shouldTakeover(user: UserData | undefined): boolean {
  return returnFlowType(user) !== null
}

// The user query refetches, so a verdict the server has not yet processed can
// re-arm the flow after it has already run. Keyed by user because logout is a
// pure state reset with no reload — a global flag would follow the next account
// signing in on the same tab. Not persisted: a genuine reload should re-show the
// flow while the server still considers it unseen.
const finishedThisSession = new Set<string>()

export function markReturnFlowFinished(userId: string): void {
  finishedThisSession.add(userId)
}

// True once the takeover has exited this session. Read by the day-intro gate: a
// returner who just walked it has had their day introduced, so its storytelling
// must not then open — the day is only still NotStarted because they closed
// before completing the comeback.
export function hasReturnFlowFinished(userId: string | undefined): boolean {
  return !!userId && finishedThisSession.has(userId)
}

// Read by the takeover redirect and the onboarding redirect, and by nothing
// else. Keep it out of the exposure report: predicting the render is the bias
// that report exists to avoid.
export function isReturnFlowPending(user: UserData | undefined): boolean {
  if (!user) return false
  // The layout redirect and the onboarding page both send a stoic user to
  // /plan, so no web surface takes them to /return. The serve is left standing
  // for iOS and Android, which know nothing of stoic.
  if (user.isStoic) return false
  return !finishedThisSession.has(user.id) && shouldTakeover(user)
}
